import React, { useRef, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import Color from "color";
import { Stage } from "./types";

interface StageStepperProps {
  stages: Stage[];
}

const STEP_WIDTH = 88; // Fixed width per step as per Figma
const VISIBLE_STEPS = 3; // Show only 3 steps at a time
const STEPPER_GREEN_DARK = "#326054";
const STEPPER_GREEN_LIGHT = "#cfe8e2";

const StageStepper: React.FC<StageStepperProps> = ({ stages }) => {

   const theme = useTheme();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollPosition, setScrollPosition] = useState(0);

  const sortedStages = [...stages].sort((a, b) => a.order - b.order);
  const maxScroll = Math.max(
    0,
    (sortedStages.length - VISIBLE_STEPS) * STEP_WIDTH
  );

  const handleScrollLeft = () => {
    if (scrollContainerRef.current) {
      const newPosition = Math.max(0, scrollPosition - STEP_WIDTH);
      scrollContainerRef.current.scrollTo({
        left: newPosition,
        behavior: "smooth",
      });
      setScrollPosition(newPosition);
    }
  };

  const handleScrollRight = () => {
    if (scrollContainerRef.current) {
      const newPosition = Math.min(maxScroll, scrollPosition + STEP_WIDTH);
      scrollContainerRef.current.scrollTo({
        left: newPosition,
        behavior: "smooth",
      });
      setScrollPosition(newPosition);
    }
  };

  const canScrollLeft = scrollPosition > 0;
  const canScrollRight = scrollPosition < maxScroll;

  const baseColor = theme.nawy.colors.semantic.layer.primary;
  const leftGradient = `linear-gradient(90deg, ${Color(baseColor).alpha(
    1
  )} 0%, ${Color(baseColor).alpha(0.9)} 40%, ${Color(baseColor).alpha(
    0
  )} 100%)`;
  const rightGradient = `linear-gradient(270deg, ${Color(baseColor).alpha(
    1
  )} 0%, ${Color(baseColor).alpha(0.9)} 40%, ${Color(baseColor).alpha(
    0
  )} 100%)`;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        padding: `${theme.nawy.spacing.sm} 0`,
        backgroundColor: theme.nawy.colors.semantic.layer.primary,
      }}
    >
      {/* Left fade gradient + Arrow */}
      <Box
        onClick={handleScrollLeft}
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: "40px",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          paddingLeft: theme.nawy.spacing.xxs,
          background: leftGradient,
          cursor: canScrollLeft ? "pointer" : "default",
          zIndex: 2,
        }}
      >
        <ChevronLeft
          size={16}
          color={theme.nawy.colors.semantic.text.secondary}
          style={{ opacity: canScrollLeft ? 1 : 0.4 }}
        />
      </Box>

      {/* Scrollable Container */}
      <Box
        ref={scrollContainerRef}
        sx={{
          display: "flex",
          overflow: "hidden",
          scrollBehavior: "smooth",
          paddingLeft: theme.nawy.spacing.lg,
          paddingRight: theme.nawy.spacing.lg,
        }}
      >
        {sortedStages.map((stage, index) => {
          const isCompleted = stage.status === "completed";
          const isCurrent = stage.status === "current";
          const isFirst = index === 0;
          const isLast = index === sortedStages.length - 1;        

          // Determine line colors based on status
          const leftLineColor =
            isCompleted || isCurrent
              ? STEPPER_GREEN_LIGHT
              : theme.nawy.colors.semantic.border.secondary;
          const rightLineColor =
            sortedStages[index + 1]?.status === "completed" ||
            sortedStages[index + 1]?.status === "current"
              ? STEPPER_GREEN_LIGHT
              : theme.nawy.colors.semantic.border.secondary;

          return (
            <Box
              key={stage.id}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                width: `${STEP_WIDTH}px`,
                minWidth: `${STEP_WIDTH}px`,
                height: "40px",
                gap: theme.nawy.spacing.xs,
              }}
            >
              {/* Checkbox Row */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                {/* Left Line - starts from checkbox for first stage */}
                {isFirst ? (
                  <Box sx={{ flex: 1 }} />
                ) : (
                  <Box
                    sx={{
                      flex: 1,
                      height: "1px",
                      backgroundColor: leftLineColor,
                    }}
                  />
                )}

                {/* Checkbox */}
                <Box
                  sx={{
                    width: theme.nawy.spacing.md,
                    height: theme.nawy.spacing.md,
                    borderRadius: "3.2px",
                    backgroundColor: isCompleted || isCurrent
                      ? STEPPER_GREEN_LIGHT
                      : theme.nawy.colors.semantic.layer.primary,
                    border: isCompleted || isCurrent
                      ? "none"
                      : `1.52px solid ${theme.nawy.colors.semantic.border.secondary}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {(isCompleted || (isLast && isCurrent) ) && (
                    <Check
                      size={12}
                      color={STEPPER_GREEN_DARK}
                      strokeWidth={2.5}
                    />
                  )}
                </Box>

                {/* Right Line */}
                {isLast ? (
                  <Box sx={{ flex: 1 }} />
                ) : (
                  <Box
                    sx={{
                      flex: 1,
                      height: "1px",
                      backgroundColor: rightLineColor,
                    }}
                  />
                )}
              </Box>

              {/* Stage Label */}
              <Typography
                sx={{
                  ...theme.nawy.typography.english.variants.body.xs.regular,
                  color:
                    isCompleted || isCurrent
                      ? STEPPER_GREEN_DARK
                      : theme.nawy.colors.semantic.text.secondary,
                  textAlign: "center",
                  width: "100%",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {stage.name}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Right fade gradient + Arrow */}
      <Box
        onClick={handleScrollRight}
        sx={{
          position: "absolute",
          right: 0,
          top: 0,
          bottom: 0,
          width: "40px",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          paddingRight: theme.nawy.spacing.xxs,
          background: rightGradient,
          cursor: canScrollRight ? "pointer" : "default",
          zIndex: 2,
        }}
      >
        <ChevronRight
          size={16}
          color={theme.nawy.colors.semantic.text.secondary}
          style={{ opacity: canScrollRight ? 1 : 0.4 }}
        />
      </Box>
    </Box>
  );
};

export default StageStepper;
